// The DEV/LOCAL FIXTURE SWITCHER — sign in as any seeded dev account from anywhere in the app, so the artist
// surface and the three roster lenses can be walked in one sitting instead of logging out between each.
// Server half: `Modules/Diagnostics/DevImpersonation.cs` (`StartAsFixtureAsync`) + `DevFixtureSwitchController`.
//
// The swapped-session state lives in `devImpersonation.ts` — one "who am I acting as" answer, one marker,
// one bearer held for the return trip. It stayed a separate module when the band-seat swap was removed
// (2026-08-31) because `useAuth` clears it on logout and the switcher button reads it; neither should have
// to import this module's fetch and scorch machinery.
//
// What bounds this, and what bounded the swap it replaced:
//   • the removed BAND swap was bounded by an authorization relationship — an owner over their own crew;
//   • this one crosses tenants with NO relationship (three unrelated orgs, plus band seats that hold no
//     authority over any of them), so it is bounded by the ADDRESS on BOTH ends: only a `.test` account
//     (RFC 2606, never-resolvable) may swap, and only into one. A real account cannot use it at all. The
//     server re-checks both on the mint; the picker's contents are not a gate.
//
// No revoke call, unlike the band swap. There is no org-scoped DELETE: a hop leaves a memory-only session
// nobody holds, which expires on its own TTL — the same outcome the band swap accepts whenever its
// best-effort revoke fails. The way back is unchanged and structural: the server withholds the re-issue
// token for an impersonated session, so a browser RELOAD resolves the admin's untouched httpOnly cookie.
//
// NATIVE: enabled since the swap writes the worn bearer with `setBearerEphemeral` (memory-only) rather than
// `setBearer` (Keychain). The Keychain keeps holding the caller's REAL token, so it plays the exact role the
// web reissue cookie plays: kill the app mid-swap and `load()` at boot resolves the real token — you land
// back on yourself. Without that, the worn bearer persisted to the Keychain and stranded a tester as the
// worn account with no marker and no way back, which is why this was web-only until now.
import type { QueryClient } from "@tanstack/react-query";
import type { TokenProvider } from "@cntrl/native";
import { API_URL, IS_NON_PRODUCTION } from "../config/env.js";
import { ApiError } from "./hooks/apiError.js";
import { resetAllStores } from "./stores.js";
import { useDevImpersonation, swapAuthority, type ActingAs } from "./devImpersonation.js";

/** A fixture account as the picker renders it (the server's `DevFixtureAccount`). */
export interface FixtureAccount {
  personId: string;
  displayName: string;
  email: string;
  /** The org or band this seat belongs to — the group heading, and what the picker groups on. */
  tenant: string;
  /** The caller's own row — rendered as "You", never swappable. */
  isSelf: boolean;
  /**
   * `"Admin"` (the org's founding, seat-managing account) or `"Owner"` (a band's owner), else null — read
   * server-side off the fixture account graph, because the seat tables it would otherwise come from are
   * RLS-fenced to the caller's own tenant. It is the row that can actually exercise the admin surfaces
   * (Team, Organization settings), which is what you are usually hunting for in this list.
   */
  badge: string | null;
  /**
   * The seat's title label (e.g. "Tour Manager", "A&R", "FOH Engineer"), else null — shown as the role chip
   * beside the name. Resolved server-side from the fixture account graph (who holds which title) plus
   * `core.seat_titles` (the label); the seat tables themselves are RLS-fenced, so this is the graph's word.
   */
  role: string | null;
  /**
   * The seat's granted ROLE key (e.g. `band_member_av`), so the role chip draws the crimson AV mark for a
   * full-access band member (ART-171) — the label alone reads "Band Member" for both full and limited.
   */
  roleKey: string | null;
  /**
   * The tenant's KIND — "artist" (a band), or an org type ("label" / "management" / "booking"), else null.
   * The picker splits its left rail into these sections. From the fixture graph, like `badge`/`role`.
   */
  category: string | null;
  /** The band is on a Pro plan — shown as a "Pro" chip beside the band in the rail. Only ever true for an
   *  artist tenant. From the fixture graph's plan + `core.plans.tier`, never a plan-key string match. */
  pro: boolean;
}

interface MintedSwap {
  token: string;
  sessionId: string;
  expiresAt: string;
  personId: string;
  displayName: string;
  email: string;
}

const DEV_URL = `${API_URL}/api/v1/dev/impersonate`;

/**
 * The route is `[ApiExplorerSettings(IgnoreApi)]`, so it is absent from the generated client on purpose —
 * a dev-only surface has no business in the committed OpenAPI snapshot or the CI drift gates. Hence the raw
 * fetch, exactly as the band swap and the `/debug` page do it.
 */
async function devFetch<T>(
  bearer: string | null,
  url: string,
  method: "GET" | "POST",
  body?: unknown,
): Promise<T | null> {
  // Belt and braces with the server's lane gate: a production build must not even form the request.
  if (!IS_NON_PRODUCTION) return null;

  const res = await fetch(url, {
    method,
    headers: {
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  // 404 is the production refusal AND a genuinely missing route — both mean "not available here".
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(res.status, undefined);

  const doc = (await res.json()) as { data?: T };
  return doc?.data ?? null;
}

/**
 * Could this lane offer the switcher at all? Deliberately NOT the whole gate — it is the cheap half that
 * must be decided client-side, so a production build never even forms the request.
 *
 * <b>WHO may switch is the SERVER's answer alone</b>, and this used to duplicate it: the client re-checked
 * the signed-in address for `.test`, which silently excluded the very account the
 * `DevSwitcher:Initiators` allowlist exists for. The rule was written in two places and only one was
 * widened, so dev's founding artist had a server that would serve it and a client that never asked
 * (2026-08-31). The button now renders on whether the LIST came back with anything, which cannot drift
 * from what the route will actually do.
 *
 * `isNative` because the swapped bearer must stay MEMORY-ONLY: `NativeTokenProvider` persists to the
 * Keychain, which would survive a relaunch with no marker and no way back.
 */
export function switcherPossibleInLane(
  // Injected, defaulted, so the predicate is testable without stubbing the env module.
  nonProductionLane: boolean = IS_NON_PRODUCTION,
): boolean {
  // Native is no longer excluded: the swap uses `setBearerEphemeral`, so the worn bearer never reaches the
  // Keychain and a relaunch returns the tester to themselves (see this module's header). Lane is the only
  // client-side gate; WHO may switch is the server's answer via the list route.
  return nonProductionLane;
}

/** Every fixture account on this box, for the picker. Admin-only server-side. */
export async function fetchFixtureAccounts(bearer: string | null): Promise<FixtureAccount[]> {
  const doc = await devFetch<{ items: FixtureAccount[] }>(bearer, DEV_URL, "GET");
  return doc?.items ?? [];
}

export interface FixtureSwitchDeps {
  tokenProvider: TokenProvider;
  queryClient: QueryClient;
  /** Land at the app root — the new identity's own shell and lens resolve from there. */
  navigate: (to: string) => void | Promise<void>;
}

/**
 * Switch to a fixture account and land at the app root.
 *
 * The scorch is the FULL one, not the roster artist's scoped variant: this changes the PERSON, and every
 * cached read belongs to the previous one. `switchRosterArtist` clears only the departing band's keys, which
 * is right for an artist hop within one identity and badly wrong here — the org, navigation and `/me`
 * entries would all survive and repaint under someone else.
 *
 * The admin's bearer is captured BEFORE the flip and handed to the store, which is what makes hopping work:
 * the route is admin-only, so once you are wearing a staffer's seat the current bearer would be refused by
 * it. Nothing in the chain is authorized by the seat you happen to be wearing.
 */
export async function switchToFixtureAccount(
  deps: FixtureSwitchDeps,
  personId: string,
): Promise<ActingAs> {
  const ownBearer = swapAuthority(deps.tokenProvider);

  const swap = await devFetch<MintedSwap>(ownBearer, DEV_URL, "POST", { personId });
  if (!swap?.token) throw new ApiError(500, undefined);

  // EPHEMERAL, never setBearer: the worn bearer stays in memory and never reaches the Keychain, so on native a
  // relaunch mid-swap resolves the caller's real token back via load() — the native analogue of the web reload
  // landing on the untouched httpOnly cookie. This is what makes the switcher safe on native at all.
  await deps.tokenProvider.setBearerEphemeral(swap.token);

  await deps.queryClient.cancelQueries();
  deps.queryClient.clear();
  resetAllStores();

  const actingAs: ActingAs = {
    personId: swap.personId,
    displayName: swap.displayName,
    email: swap.email,
    sessionId: swap.sessionId,
  };
  useDevImpersonation.getState().begin(actingAs, ownBearer);

  // The ROOT, not a lens route: the new identity may be a band seat or an org one, and the router's own
  // shell selection is what knows which. Sending an org admin to "/" lands them on the roster; sending a
  // band owner to "/roster" would bounce.
  await deps.navigate("/");

  return actingAs;
}

/**
 * Return to the account this session started as — the switcher dialog's "Return to my account".
 *
 * Mirrors {@link switchToFixtureAccount} in reverse: restore the held bearer, scorch, land at the root.
 *
 * No revoke, for the reason the module header gives: there is no route for one, and the abandoned session is
 * memory-only and expires on its TTL. The bearer restore is what actually returns you.
 */
export async function returnFromFixtureSwitch(deps: FixtureSwitchDeps): Promise<boolean> {
  const { actingAs, ownBearer } = useDevImpersonation.getState();
  if (!actingAs) return false;

  // Ephemeral again: on native the Keychain still holds this same real token untouched, so we are only
  // restoring the in-memory mirror. On web it is identical to setBearer (memory-only there anyway).
  if (ownBearer) await deps.tokenProvider.setBearerEphemeral(ownBearer);

  await deps.queryClient.cancelQueries();
  deps.queryClient.clear();
  resetAllStores();

  // Last, for the reason switchToFixtureAccount documents: the identity flip drives the remount, and the
  // remount must land on an already-scorched cache with nothing in flight.
  useDevImpersonation.getState().end();

  await deps.navigate("/");

  return true;
}
